import { Injectable } from '@angular/core';
import { CoreApiService, RequestConfig } from '../core-api-service';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { API_URLS } from 'app/modules/core/constants/core.constants';
import { convertBase64ToBlob, joinAddressParts, parseCurrency } from 'app/modules/shared/utility/helperMethods';
import { PolicyQuoteQuestionSearch } from '@dtos/search-parameters/search-parameters';
import { QuestionDTO, QuestionnaireDTO } from '@shared/components/questionnaire/models';
import {
  DriverPdfDTO,
  ExportHomeQuotePdfDTO,
  HealthQuoteDependantInsertPdfDTO,
  HealthQuotePdf,
  HealthQuoteQuestionnaireDetailsPdfDTO,
  HealthQuoteQuestionnairePdfDTO,
  ExportMotorQuotePdfDTO,
  PolicyQuoteQuestionListDTO,
  QuestionPdfDTO,
  QuestionnaireMotorPdfDTO,
  TrailerPdfDTO,
  VehiclePdfDTO,
  DrivingLicenseDTO,
  SubmitQuoteRes,
  SubmitMotorQuotationParamDTO,
  SubmitHomeQuotationParamDTO,
  ValuablesIncluded
} from 'models/DTOs';
import {
  HealthQuoteInsertDTO,
  HomeQuoteDTO,
  MotorQuoteDTO,
  DraftQuote,
  HomeQuotePages
} from 'app/modules/quotes/models/models';
import { StepDTO } from 'app/modules/quotes/quote-stepper/quote-stepper.component';
import { formatDate } from '@angular/common';
import { SelectedImage } from '@shared/components/drag-n-drop/drag-n-drop.component';
import { UserProfile } from 'app/services/user.service';

@Injectable({
  providedIn: 'root'
})
export class QuotesService extends CoreApiService {
  quoteStepperSubject: BehaviorSubject<StepDTO[]> = new BehaviorSubject(null);

  getQuestionnaire(req: PolicyQuoteQuestionSearch) {
    return this.get<PolicyQuoteQuestionListDTO[]>(API_URLS.policyQuoteQuestions, { params: { ...req } }).pipe(
      map(response => {
        const questionnaire: QuestionnaireDTO[] = [];
        response = response.sort((a, b) => a.questionGroupSequence - b.questionGroupSequence);

        let sequence = -1;
        response.forEach(q => {
          if (sequence !== q.questionGroupSequence) {
            sequence++;
            const questionGroup: QuestionnaireDTO = {
              questionnaireID: String(q.questionGroupSequence),
              questionGroupText: q.questionGroupText,
              questionGroupHeader: q.questionGroupHeader,
              questionnaireType: q.quoteType,
              questionGroupSequence: q.questionGroupSequence,
              questions: response
                .filter(x => x.questionGroupSequence === q.questionGroupSequence)
                .map((nestedQItem: PolicyQuoteQuestionListDTO) => {
                  const question: QuestionDTO = {
                    questionID: nestedQItem.policyQuoteQuestionID,
                    questionText: nestedQItem.questionHeader,
                    questionTip: nestedQItem.questionText
                  };
                  return question;
                })
            };
            questionnaire.push(questionGroup);
          }
        });
        return questionnaire;
      })
    );
  }

  createPdfHomeQuote(homeQuote: DraftQuote, profile: UserProfile) {
    const pageData = (homeQuote.quote as HomeQuoteDTO)?.pageData;
    const startDate = homeQuote?.startDate
      ? formatDate(homeQuote.startDate, 'MM/dd/yyyy', 'en-US')
      : formatDate(pageData[HomeQuotePages.PERSONALIZED_QUOTES].data?.startDate, 'MM/dd/yyyy', 'en-US');
    const endDate = new Date(startDate);
    endDate.setFullYear(endDate.getFullYear() + 1);
    endDate.setDate(endDate.getDate() - 1);

    let playingFields = 0; //Quantity
    let seawallsDocksJetties = 0;
    let poolsOrPonds = 0;
    let solarPanels = 0;
    let playingFieldsQuantity = 0;
    let seawallsDocksJettiesQuantity = 0;
    let poolsOrPondsQuantity = 0;
    let solarPanelsQuantity = 0;
    let other = 0;
    let otherDescription = '';

    pageData[HomeQuotePages.BUILDING_COVER].data?.externalStructuresList?.forEach(item => {
      switch (item.externalStructureType) {
        case 'Playing Field':
          playingFields += parseCurrency(item.value);
          playingFieldsQuantity++;
          break;
        case 'Seawall/Dock/Jettie':
          seawallsDocksJetties += parseCurrency(item.value);
          seawallsDocksJettiesQuantity += 1;
          break;
        case 'Pool/Pond':
          poolsOrPonds += parseCurrency(item.value);
          poolsOrPondsQuantity++;
          break;
        case 'Solar Panel':
          solarPanels += parseCurrency(item.value);
          solarPanelsQuantity++;
          break;
        case 'Other':
          other += parseCurrency(item.value);
          otherDescription = item.description;
          break;
      }
    });

    const params: Partial<ExportHomeQuotePdfDTO> = {
      coverageType: pageData[HomeQuotePages.TYPE_OF_COVERAGE].data?.coverageType,
      otherNo: profile.phone2,
      cellNo: profile.phone,
      emailAddress: profile.email,
      mailingAddress: joinAddressParts(
        profile.country,
        profile.island,
        profile.district,
        profile.street,
        profile.postalCode
      ),
      // HomeQuotePages.HOME_ADDRESS

      houseNumber: pageData[HomeQuotePages.HOME_ADDRESS].data?.houseNumber,
      streetName: pageData[HomeQuotePages.HOME_ADDRESS].data?.propertyAddress,
      propertyName: pageData[HomeQuotePages.HOME_ADDRESS].data?.propertyName,
      district: pageData[HomeQuotePages.HOME_ADDRESS].data?.district,
      island: pageData[HomeQuotePages.HOME_ADDRESS].data?.island,
      location: pageData[HomeQuotePages.HOME_ADDRESS].data?.location,

      // HomeQuotePages.PROPERTY_TYPE

      propertyUsage: pageData[HomeQuotePages.PROPERTY_TYPE].data?.propertyUsage,
      propertyType: pageData[HomeQuotePages.PROPERTY_TYPE].data?.propertyType,
      //propertyTypeOther: pageData[HomeQuotePages.PROPERTY_TYPE].data?.propertyTypeOther,
      propertyStories: pageData[HomeQuotePages.PROPERTY_TYPE].data?.propertyStories,
      yearsConstructed: pageData[HomeQuotePages.PROPERTY_TYPE].data?.yearsConstructed,
      squareFeet: pageData[HomeQuotePages.PROPERTY_TYPE].data?.squareFeet,

      // HomeQuotePages.BUILDING_MATERIALS

      wallMaterial: pageData[HomeQuotePages.BUILDING_MATERIALS].data?.wallMaterial,
      roofMaterial: pageData[HomeQuotePages.BUILDING_MATERIALS].data?.roofMaterial,
      hurricaneStraps: pageData[HomeQuotePages.BUILDING_MATERIALS].data?.hurricaneStraps,

      // HomeQuotePages.BUILDING_COVER

      addBuildingsCover: String(pageData[HomeQuotePages.BUILDING_COVER].data?.addBuildingsCover),
      totalBuildingValue: pageData[HomeQuotePages.BUILDING_COVER].data?.propertyValue,
      declareExternalStructures: String(pageData[HomeQuotePages.BUILDING_COVER].data?.declareExternalStructures),
      playingFields: '$ ' + String(playingFields), //pageData[HomeQuotePages.BUILDING_COVER].data?.playingFields,
      seawallsDocksJetties: '$ ' + String(seawallsDocksJetties), //pageData[HomeQuotePages.BUILDING_COVER].data?.seawallsDocksJetties,
      poolsOrPonds: '$ ' + String(poolsOrPonds), //pageData[HomeQuotePages.BUILDING_COVER].data?.poolsOrPonds,
      solarPanels: '$ ' + String(solarPanels), //pageData[HomeQuotePages.BUILDING_COVER].data?.solarPanels,
      other: '$ ' + String(other), // pageData[HomeQuotePages.BUILDING_COVER].data?.other,
      otherDescription: otherDescription, //pageData[HomeQuotePages.BUILDING_COVER].data?.otherDescription,
      //
      mainBuildingQuantity: pageData[HomeQuotePages.BUILDING_COVER].data?.mainBuildingQuantity,
      detachedBuildingsQuantity: pageData[HomeQuotePages.BUILDING_COVER].data?.detachedBuildingsQuantity,
      playingFieldsQuantity: String(playingFieldsQuantity), //pageData[HomeQuotePages.BUILDING_COVER].data?.playingFieldsQuantity,
      seawallDocksJettiesQuantity: String(seawallsDocksJettiesQuantity), //pageData[HomeQuotePages.BUILDING_COVER].data?.seawallDocksJettiesQuantity,
      swimmingPoolsQuantity: String(poolsOrPondsQuantity), //pageData[HomeQuotePages.BUILDING_COVER].data?.swimmingPoolsQuantity,
      solarPanelsQuantity: String(solarPanelsQuantity), //pageData[HomeQuotePages.BUILDING_COVER].data?.solarPanelsQuantity,
      mainBuildingBuildingCover: pageData[HomeQuotePages.BUILDING_COVER].data?.mainBuilding,
      detachedBuilding: pageData[HomeQuotePages.BUILDING_COVER].data?.detachedBuilding,

      // HomeQuotePages.CONTENT_COVER

      addContentCover: String(pageData[HomeQuotePages.CONTENT_COVER].data?.addContentCover),
      totalContentValue: String(pageData[HomeQuotePages.CONTENT_COVER].data?.totalContentValue),
      valuablesExceeding: String(pageData[HomeQuotePages.CONTENT_COVER].data?.valuablesExceeding),

      // HomeQuotePages.HOME_RISK_COVER

      // allRiskCover: String(pageData[HomeQuotePages.HOME_RISK_COVER].data?.allRiskCover),
      // totalRiskValue: pageData[HomeQuotePages.HOME_RISK_COVER].data?.totalRiskValue,
      // coverAnyItems: String(pageData[HomeQuotePages.HOME_RISK_COVER].data?.coverAnyItems),

      // HomeQuotePages.HOME_IMPROVEMENTS_COVER

      addImprovementsBettermentsCover: String(
        pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.addImprovementsBettermentsCover
      ),
      totalImprovementsValue: pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.totalImprovementsValue,
      mainBuilding: pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.mainBuilding,
      detachedBuildingsOrGarages: pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.detachedBuildingsOrGarages,
      tennisCourts: pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.tennisCourts,
      seawallsDocksJettiesImprovements:
        pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.seawallsDocksJettiesImprovements,
      swimmingPools: pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.swimmingPools,
      permanentSolarPanels: pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.permanentSolarPanels,
      otherImprovements: pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.otherImprovements,
      pleaseDescribeWhatOtherIncludes:
        pageData[HomeQuotePages.HOME_IMPROVEMENTS_COVER].data?.pleaseDescribeWhatOtherIncludes,
      // HomeQuotePages.CONFIRM_OWNER_DETAILS

      fullName: pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerFullName,
      dateOfBirth: formatDate(
        pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerDateOfBirth,
        'MM/dd/yyyy',
        'en-US'
      ),
      genderType: String(pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerGenderType),
      issuedDate: formatDate(
        pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerIssuedDate,
        'MM/dd/yyyy',
        'en-US'
      ),
      expiryDate: formatDate(
        pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerExpiryDate,
        'MM/dd/yyyy',
        'en-US'
      ),
      occupation: pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerOccupation,
      nationalIdType: String(pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerNationalIdType),
      nationalIdNumber: String(pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerNationalIdNumber),
      countryOfIssue: String(pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.ownerCountryOfIssue),
      nationality: pageData[HomeQuotePages.CONFIRM_OWNER_DETAILS].data?.nationality,

      // HomeQuotePages.PERSONALIZED_QUOTES

      startDate: homeQuote?.startDate
        ? formatDate(homeQuote.startDate, 'MM/dd/yyyy', 'en-US')
        : formatDate(pageData[HomeQuotePages.PERSONALIZED_QUOTES].data?.startDate, 'MM/dd/yyyy', 'en-US'),
      endDateCover: formatDate(endDate, 'MM/dd/yyyy', 'en-US'),
      subjectLoan: null,
      selectMortgagees: null,
      unoccupied: null,
      convicted: null,
      unoccupiedValue: null,
      convictedValue: null,
      insurerDeclinedAccept: null,
      insurerDeclinedAcceptValue: null,

      // HomeQuotePages.ADDITIONAL_INSURERS

      additionalInsurers: null,
      valuablesIncludedList: [],
      // HomeQuotePages.CLIENT_HISTORY

      stateOfRepairs: null,
      pastClaims: null,
      stateOfRepairDetails: null,
      pastClaimsDetails: null,

      signatureUrl: homeQuote.signatureUrl,
      isSigned: homeQuote?.isSigned,
      isSubmited: homeQuote?.isSubmited,
      isDeleted: homeQuote?.isDeleted
    };
    if (pageData[HomeQuotePages.COVER_FINALIZATION]?.data) {
      params.subjectLoan = String(pageData[HomeQuotePages.COVER_FINALIZATION].data?.subjectLoan);
      params.selectMortgagees = String(pageData[HomeQuotePages.COVER_FINALIZATION].data?.selectMortgagees);
      params.unoccupied = String(pageData[HomeQuotePages.COVER_FINALIZATION].data?.unoccupied);
      params.convicted = String(pageData[HomeQuotePages.COVER_FINALIZATION].data?.convicted);
      params.unoccupiedValue = String(pageData[HomeQuotePages.COVER_FINALIZATION].data?.unoccupiedValue);
      params.convictedValue = pageData[HomeQuotePages.COVER_FINALIZATION].data?.convictedValue;
      params.insurerDeclinedAccept = String(pageData[HomeQuotePages.COVER_FINALIZATION].data?.insurerDeclinedAccept);
      params.insurerDeclinedAcceptValue = pageData[HomeQuotePages.COVER_FINALIZATION].data?.insurerDeclinedAcceptValue;
      params.partOfSTRATA = pageData[HomeQuotePages.COVER_FINALIZATION].data?.partOfSTRATA;
      params.strataValue = pageData[HomeQuotePages.COVER_FINALIZATION].data?.strataValue;
    }
    /* * */
    params.valuablesIncludedList = pageData[HomeQuotePages.CONTENT_COVER].data?.valuablesIncludedList?.map(item => {
      const valuablesIncluded: ValuablesIncluded = {
        description: item.description,
        value: item.value,
        qty: '',
        sumInsured: '',
        model: '',
        marke: '',
        allRisk: ''
      };
      return valuablesIncluded;
    });
    /* * */
    if (pageData[HomeQuotePages.ADDITIONAL_INSURERS]?.data)
      params.additionalInsurers = pageData[HomeQuotePages.ADDITIONAL_INSURERS]?.data?.additionalInsurers;

    if (pageData[HomeQuotePages.ADDITIONAL_INSURERS]?.data) {
      params.stateOfRepairs = pageData[HomeQuotePages.CLIENT_HISTORY].data?.stateOfRepairs;
      params.pastClaims = pageData[HomeQuotePages.CLIENT_HISTORY].data?.pastClaims;
      params.stateOfRepairDetails = pageData[HomeQuotePages.CLIENT_HISTORY].data?.stateOfRepairDetails;
      params.pastClaimsDetails = pageData[HomeQuotePages.CLIENT_HISTORY].data?.pastClaimsDetails;
    }

    const requestConfig: RequestConfig = {
      responseType: 'blob'
    };

    return this.post(API_URLS.exportHomeQuote, params, requestConfig);
  }

  createPdfMotorQuote(quote: DraftQuote, profile: UserProfile) {
    const motorQuote = quote.quote as MotorQuoteDTO;
    const questionnairePdf: QuestionnaireMotorPdfDTO = {
      questionGroupHeader: motorQuote.questionnaire.questionGroupHeader,
      questionGroupText: motorQuote.questionnaire.questionGroupText,
      questionnaireID: motorQuote.questionnaire.questionnaireID,
      questionnaireType: String(motorQuote.questionnaire.questionnaireType),
      questions: []
    };

    questionnairePdf.questions = motorQuote.questionnaire.questions?.map(item => {
      const questionPdf: QuestionPdfDTO = {
        questionText: item.questionText,
        questionTip: item.questionTip,
        isAnsweredYes: String(item.isAnsweredYes),
        answerDescription: item.answerDescription
      };
      return questionPdf;
    });

    const endDateVal = new Date(motorQuote.startDate); //motorQuote.startDate;
    endDateVal.setFullYear(endDateVal.getFullYear() + 1);
    endDateVal.setDate(endDateVal.getDate() - 1);

    const params: ExportMotorQuotePdfDTO = {
      typeOfCoverage: motorQuote.typeOfCoverage,
      driversInfo: [],
      questionnaires: questionnairePdf,
      civilServants: motorQuote.civilServants,
      memberId: motorQuote.memberId,
      safeDriverProgram: motorQuote.safeDriverProgram,
      certificateNo: motorQuote.certificateNo,
      multiPolicy: motorQuote.multiPolicy,
      policyNumber: motorQuote.policyNumber,
      seniorCitizen: motorQuote.seniorCitizen,
      cinicoStaff: motorQuote.cinicoStaff,
      startDate: formatDate(motorQuote.startDate, 'MM/dd/yyyy', 'en-US'),
      endDate: formatDate(endDateVal, 'MM/dd/yyyy', 'en-US'),
      areThereExistingDamages: motorQuote.areThereExistingDamages,
      vehicles: [],
      trailers: [],
      //owner
      title: profile.title,
      fullName: motorQuote.fullName,
      occupation: profile.occupation,
      dateOfBirth: formatDate(motorQuote.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
      genderType: motorQuote.genderType,
      moreThanYears: motorQuote.moreThanYears,
      typeOfLicense: motorQuote.typeOfLicense,
      licenseGroup: motorQuote.licenseNo, //motorQuote.licenseGroup.toString(),
      //licenseNo: motorQuote.licenseNo,
      countryOfIssue: motorQuote.countryOfIssue,
      firstIssuedDate: formatDate(motorQuote.firstIssuedDate, 'MM/dd/yyyy', 'en-US'),
      issuedDate: formatDate(motorQuote.issuedDate, 'MM/dd/yyyy', 'en-US'),
      expiryDate: formatDate(motorQuote.expiryDate, 'MM/dd/yyyy', 'en-US'),
      vehicleUsedFor: '',
      describeDamages: motorQuote.describeDamages,
      signatureUrl: quote.signatureUrl,
      isSigned: quote?.isSigned,
      isSubmited: quote?.isSubmited,
      isDeleted: quote?.isDeleted,
      mailingAddress: profile.poBox + ', ' + profile.postalCode,
      unit: '', //profile.poBox,
      street: profile.street,
      district: profile.district,
      telNo: profile.phone,
      ownerOther: profile.phone2,
      email1: profile.email,
      email2: profile.email2,
      completedProposal: '',
      owenerDriversLicense: '',
      proofOfNoClaimDiscount: '',
      vehicleLogbook: '',
      newVehicleInvoice: '',
      articlesOfIncorporation: ''
    };
    params.driversInfo = motorQuote.drivers?.map(item => {
      const driver: DriverPdfDTO = {
        age: null,
        fullName: item.fullName,
        gender: String(item.genderType),
        experience: '',
        dateOfBirth: formatDate(item.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
        genderType: item.genderType,
        moreThanYears: item.moreThanYears,
        licenseNo: item.licenseNo,
        typeOfLicense: item.typeOfLicense,
        licenseGroup: String(item.licenseGroup),
        countryOfIssue: item.countryOfIssue,
        firstIssuedDate: formatDate(item.firstIssuedDate, 'MM/dd/yyyy', 'en-US'),
        issuedDate: formatDate(item.issuedDate, 'MM/dd/yyyy', 'en-US'),
        expiryDate: formatDate(item.expiryDate, 'MM/dd/yyyy', 'en-US'),
        relationshipToInsured: item.relationshipToInsured
      };
      return driver;
    });
    params.vehicles = motorQuote.vehicles?.map(item => {
      const vehicle: VehiclePdfDTO = {
        vehicleUsedFor: '',
        vehicleYear: item.vehicleYear,
        vehicleOrigin: item.vehicleOrigin,
        vehicleModel: item.vehicleModel,
        vehicleMake: item.vehicleMake,
        vehicleBodyType: item.vehicleBodyType,
        isTheVehicleMortgaged: item.isTheVehicleMortgaged,
        vehicleMortgageName: item.vehicleMortgageName,
        vehicleVINChassisNo: item.vehicleVINChassisNo,
        vehicleRegistrationNo: String(item.vehicleRegistrationNo),
        vehicleIslandLocation: item.vehicleIslandLocation,
        vehicleEngineType: item.vehicleEngineType,
        vehicleEngineSize: item.vehicleEngineSize,
        vehicleCurrentValue: item.vehicleCurrentValue,
        modificationsAccessories: item.modificationsAccessories,
        engineChangedType: item.engineChangedType,
        engineChangedCapacity: item.engineChangedCapacity,
        engineChangedTunedValue: item.engineChangedTunedValue,
        antiTheftDeviceValue: item.antiTheftDeviceValue,
        antiTheftDeviceType: item.antiTheftDeviceType,
        wheelsAndRimsValue: item.wheelsAndRimsValue,
        wheelsAndRimsType: item.wheelsAndRimsType,
        otherValue: item.otherValue,
        otherType: item.otherType,
        yourPolicyIncludesWindscreenCover: item.yourPolicyIncludesWindscreenCover,
        additionalValue: item.additionalValue,
        excessWaiverBenefit: item.excessWaiverBenefit,
        checkboxEngineChangedTuned: String(item.checkboxEngineChangedTuned),
        checkboxAntiTheftDevice: String(item.checkboxAntiTheftDevice),
        checkboxWheelsAndRims: String(item.checkboxWheelsAndRims),
        checkboxOther: String(item.checkboxOther),
        additionalNoClaimDiscountProtection: item.additionalNoClaimDiscountProtection,
        discountPeriod: item.discountPeriod,
        //
        vehiclEngineNo: item.vehicleEngineNumber,
        vehiclColor: item.vehicleColour,
        vehiclEngineCapacity: item.engineChangedCapacity,
        vehiclMileage: item.vehicleMileage,
        vehiclOvernightParking: item.overnightParking,
        vehiclTotalPassengers: item.vehicleTotalPassengers,
        vehiclDetailsOfAnyexistingDamages: item.existingDamageDetails,
        vehiclAnyOtherInsuranceCompany: item.vehicleOtherInsuranceCompany,
        vehiclAnyOtherPurpose: item.vehicleWillTheVehicleExplain
      };
      return vehicle;
    });

    params.trailers = motorQuote.trailers?.map(item => {
      const trailer: TrailerPdfDTO = {
        trailerLength: Number(item.trailerLength),
        trailerMake: item.trailerMake,
        trailerModel: item.trailerModel
      };
      return trailer;
    });

    return this.post(API_URLS.exportMotorQuote, params, { responseType: 'blob' });
  }

  createPdfHealthQuote(healthQuote: HealthQuoteInsertDTO) {
    const pdfModel: HealthQuotePdf = {
      applicantAddress: healthQuote.applicantAddress,
      applicantDateOfBirth: formatDate(healthQuote.applicantDateOfBirth, 'MM/dd/yyyy', 'en-US'), //healthQuote.applicantDateOfBirth ? new Date(healthQuote.applicantDateOfBirth).toLocaleDateString() : '',
      applicantDistrict: healthQuote.applicantDistrict,
      applicantEmail: healthQuote.applicantEmail,
      applicantFirstName: healthQuote.applicantFirstName,
      applicantGenderType: String(healthQuote.applicantGenderType),
      applicantHeight: healthQuote.applicantHeight,
      applicantLastName: healthQuote.applicantLastName,
      applicantMiddleName: healthQuote.applicantMiddleName,
      applicantPOBox: healthQuote.applicantPOBox,
      applicantPhone: healthQuote.applicantPhone,
      applicantPostalCode: healthQuote.applicantPostalCode,
      applicantWeight: healthQuote.applicantWeight,
      employerAddress: healthQuote.employerAddress,
      employerCode: healthQuote.employerCode,
      employerEmail: healthQuote.employerEmail,
      employerName: healthQuote.employerName,
      employerPOBox: healthQuote.employerPOBox,
      employerPhone: healthQuote.employerPhone,
      employerPostalCode: healthQuote.postalCode,
      employmentStatusType: String(healthQuote.employmentStatusType),
      questionnaires: [],
      healthQuoteDependants: []
    };

    pdfModel.questionnaires = healthQuote.questionnaires?.map(item => {
      const questionmaire: HealthQuoteQuestionnairePdfDTO = {
        questionGroupHeader: item.questionGroupHeader,
        questionGroupText: item.questionGroupText,
        questionnaireID: item.questionnaireID,
        questionnaireType: '',
        questions: item.questions.map(val => {
          const question: HealthQuoteQuestionnaireDetailsPdfDTO = {
            diagnosisDate: formatDate(val.diagnosisDate, 'MM/dd/yyyy', 'en-US'),
            isAnsweredYes: val.isAnsweredYes ? 'Yes' : 'No',
            medicalProviderNameAddress: val.medicalProviderNameAddress,
            patientName: val.patientName,
            questionText: val.questionText,
            questionTip: '',
            treatment: val.treatment
          };
          return question;
        })
      };
      return questionmaire;
    });

    pdfModel.healthQuoteDependants = healthQuote.healthQuoteDependants?.map(item => {
      const dependants: HealthQuoteDependantInsertPdfDTO = {
        dependantType: String(item.dependantType),
        dependantName: item.dependantName,
        genderType: String(item.genderType),
        dateOfBirth: formatDate(item.dateOfBirth, 'MM/dd/yyyy', 'en-US'),
        height: item.height,
        weight: item.weight,
        employerName: item.employerName,
        employerPhone: item.employerPhone,
        questionnaires: item.questionnaires.map(item => {
          const questionmaire: HealthQuoteQuestionnairePdfDTO = {
            questionGroupHeader: item.questionGroupHeader,
            questionGroupText: item.questionGroupText,
            questionnaireID: item.questionnaireID,
            questionnaireType: '',
            questions: item.questions.map(val => {
              const question: HealthQuoteQuestionnaireDetailsPdfDTO = {
                diagnosisDate: formatDate(val.diagnosisDate, 'MM/dd/yyyy', 'en-US'),
                isAnsweredYes: val.isAnsweredYes ? 'Yes' : 'No',
                medicalProviderNameAddress: val.medicalProviderNameAddress,
                patientName: val.patientName,
                questionText: val.questionText,
                questionTip: '',
                treatment: val.treatment
              };
              return question;
            })
          };
          return questionmaire;
        })
      };
      return dependants;
    });

    //console.log(healthQuote, 'HealthQuotePdf');

    const requestConfig: RequestConfig = {
      responseType: 'blob'
    };

    return this.post(API_URLS.exportHealthQuote, pdfModel, requestConfig).subscribe(response => {
      const url = window.URL.createObjectURL(response);
      window.open(url);
    });
  }

  uploadDrivingLicence(licences: SelectedImage[]): Observable<DrivingLicenseDTO[]> {
    const formData = new FormData();

    licences.forEach(file => {
      const fileContent = convertBase64ToBlob(file.content);
      formData.append('files', fileContent, file.name);
    });

    return this.post<DrivingLicenseDTO[]>(API_URLS.uploadDrivingLicence, formData);
  }

  submitMotorQuote(data: SubmitMotorQuotationParamDTO): Observable<SubmitQuoteRes> {
    return this.post<SubmitQuoteRes>(API_URLS.ebSubmitMotorQuote, data);
  }

  submitHomeQuote(data: SubmitHomeQuotationParamDTO): Observable<SubmitQuoteRes> {
    return this.post<SubmitQuoteRes>(API_URLS.ebSubmitHomeQuote, data);
  }

  currencyStringToNumber(value: any) {
    return Number(String(value)?.slice(2)?.replace(',', ''));
  }
}
